import React, { useEffect, useState, useRef, useCallback } from "react";
import { Button } from "@/components/ui/button";
import { Download, X as XIcon, ChevronDown, ChevronUp, Smartphone } from "lucide-react";
import { setInstalledInfo } from "@/components/ApkUpdateBanner";

const DISMISS_KEY = "dcc-install-dismissed";

/* ── Platform detection ── */
function getIsIOS() {
  if (typeof navigator === "undefined") return false;
  return (
    /iPad|iPhone|iPod/.test(navigator.userAgent) ||
    (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1)
  );
}

function getIsStandalone() {
  if (typeof window === "undefined") return false;
  return (
    window.matchMedia("(display-mode: standalone)").matches ||
    navigator.standalone === true
  );
}

function getIsAndroid() {
  if (typeof navigator === "undefined") return false;
  return /Android/i.test(navigator.userAgent);
}

/* ═══════════════════════════════════════════════
   Inline SVG illustrations for iOS instructions
   ═══════════════════════════════════════════════ */

/** Safari share-button illustration: rounded-rect toolbar with the
 *  share icon (square + up-arrow) highlighted in the centre. */
function ShareIllustration() {
  return (
    <svg
      viewBox="0 0 160 72"
      className="w-full max-w-[160px] h-auto"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden="true"
    >
      {/* Safari-style toolbar bar */}
      <rect x="4" y="8" width="152" height="56" rx="12" fill="hsl(var(--secondary))" stroke="hsl(var(--border))" strokeWidth="1.5" />

      {/* Other toolbar icons – muted dots */}
      <circle cx="30" cy="36" r="6" fill="hsl(var(--muted))" />
      <circle cx="130" cy="36" r="6" fill="hsl(var(--muted))" />

      {/* Highlight ring around the share icon */}
      <rect x="64" y="20" width="32" height="32" rx="8" fill="hsl(var(--primary) / 0.12)" stroke="hsl(var(--primary))" strokeWidth="1.5" />

      {/* Share icon: box + up-arrow */}
      <rect x="73" y="30" width="14" height="12" rx="2" stroke="hsl(var(--primary))" strokeWidth="1.5" fill="none" />
      <line x1="80" y1="38" x2="80" y2="24" stroke="hsl(var(--primary))" strokeWidth="1.5" strokeLinecap="round" />
      <polyline points="76,28 80,24 84,28" stroke="hsl(var(--primary))" strokeWidth="1.5" fill="none" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

/** "Add to Home Screen" menu item illustration: a share-sheet row
 *  with a plus-in-square icon and the label highlighted. */
function AddToHomeIllustration() {
  return (
    <svg
      viewBox="0 0 200 64"
      className="w-full max-w-[200px] h-auto"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden="true"
    >
      {/* Menu row background */}
      <rect x="4" y="4" width="192" height="56" rx="10" fill="hsl(var(--secondary))" stroke="hsl(var(--border))" strokeWidth="1.5" />

      {/* Plus-in-square icon */}
      <rect x="16" y="16" width="32" height="32" rx="7" stroke="hsl(var(--primary))" strokeWidth="1.5" fill="hsl(var(--primary) / 0.08)" />
      <line x1="32" y1="26" x2="32" y2="38" stroke="hsl(var(--primary))" strokeWidth="1.8" strokeLinecap="round" />
      <line x1="26" y1="32" x2="38" y2="32" stroke="hsl(var(--primary))" strokeWidth="1.8" strokeLinecap="round" />

      {/* Label text – styled rectangle placeholder */}
      <rect x="58" y="22" width="110" height="8" rx="3" fill="hsl(var(--foreground) / 0.7)" />
      <rect x="58" y="36" width="68" height="6" rx="3" fill="hsl(var(--muted-foreground) / 0.4)" />
    </svg>
  );
}

/** Tap "Add" button illustration: a modal header bar with the Add
 *  button highlighted in the top-right. */
function TapAddIllustration() {
  return (
    <svg
      viewBox="0 0 200 56"
      className="w-full max-w-[200px] h-auto"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden="true"
    >
      {/* Modal header bar */}
      <rect x="4" y="4" width="192" height="48" rx="10" fill="hsl(var(--secondary))" stroke="hsl(var(--border))" strokeWidth="1.5" />

      {/* Cancel text placeholder – left */}
      <rect x="16" y="22" width="44" height="8" rx="3" fill="hsl(var(--muted-foreground) / 0.4)" />

      {/* Page title placeholder – center */}
      <rect x="76" y="20" width="48" height="10" rx="3" fill="hsl(var(--foreground) / 0.6)" />

      {/* "Add" button – right, highlighted */}
      <rect x="148" y="14" width="38" height="28" rx="7" fill="hsl(var(--primary))" />
      <text x="167" y="33" textAnchor="middle" fill="hsl(var(--primary-foreground))" fontSize="11" fontWeight="700" fontFamily="sans-serif">Add</text>
    </svg>
  );
}

/* ═══════════════════════════════════════
   Main exported component
   ═══════════════════════════════════════ */

export function InstallAppCard() {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [dismissed, setDismissed] = useState(false);
  const [installed, setInstalled] = useState(false);
  const [installing, setInstalling] = useState(false);
  const [iosExpanded, setIosExpanded] = useState(false);
  const promptRef = useRef(null);

  const isIOS = getIsIOS();
  const isAndroid = getIsAndroid();
  const isStandalone = getIsStandalone();

  const backendUrl = process.env.REACT_APP_BACKEND_URL || "";

  /* Pick up globally-captured prompt from App.js, or listen for late arrivals */
  const syncPrompt = useCallback(() => {
    if (window.__dccDeferredPrompt) {
      promptRef.current = window.__dccDeferredPrompt;
      setDeferredPrompt(window.__dccDeferredPrompt);
    }
  }, []);

  useEffect(() => {
    syncPrompt();

    function onReady() { syncPrompt(); }
    function onInstalled() {
      setInstalled(true);
      promptRef.current = null;
      setDeferredPrompt(null);
    }
    /* Also listen directly in case the event fires after mount */
    function handlePrompt(e) {
      e.preventDefault();
      promptRef.current = e;
      setDeferredPrompt(e);
    }

    window.addEventListener("dcc:install-prompt-ready", onReady);
    window.addEventListener("dcc:app-installed", onInstalled);
    window.addEventListener("appinstalled", onInstalled);
    window.addEventListener("beforeinstallprompt", handlePrompt);

    return () => {
      window.removeEventListener("dcc:install-prompt-ready", onReady);
      window.removeEventListener("dcc:app-installed", onInstalled);
      window.removeEventListener("appinstalled", onInstalled);
      window.removeEventListener("beforeinstallprompt", handlePrompt);
    };
  }, [syncPrompt]);

  /* Check localStorage for dismissal */
  useEffect(() => {
    try {
      if (localStorage.getItem(DISMISS_KEY) === "1") setDismissed(true);
    } catch { /* private browsing */ }
  }, []);

  /* ── Hide conditions ── */
  if (isStandalone || installed || dismissed) return null;
  /* Android: always show APK download option */
  /* Non-iOS, non-Android: only show if prompt is available (Chromium desktop) */
  if (!isIOS && !isAndroid && !deferredPrompt) return null;

  function handleDismiss() {
    setDismissed(true);
    try { localStorage.setItem(DISMISS_KEY, "1"); } catch { /* */ }
  }

  async function handleInstallChromium() {
    const prompt = promptRef.current;
    if (!prompt) return;
    setInstalling(true);
    try {
      prompt.prompt();
      const result = await prompt.userChoice;
      if (result.outcome === "accepted") setInstalled(true);
    } catch { /* */ }
    setInstalling(false);
    promptRef.current = null;
    window.__dccDeferredPrompt = null;
    setDeferredPrompt(null);
  }

  /* ── Android APK download banner ── */
  if (isAndroid) {
    async function handleAndroidDownload() {
      // Fetch and store current version before triggering download
      try {
        const res = await fetch(`${backendUrl}/api/app/version`);
        if (res.ok) {
          const data = await res.json();
          setInstalledInfo(data.versionCode, data.versionName);
        }
      } catch { /* proceed with download anyway */ }
      window.location.href = `${backendUrl}/api/app/download-apk`;
    }

    return (
      <div
        data-testid="install-app-banner-android"
        className="relative flex items-center gap-3 px-4 py-2.5 mb-6 rounded-xl border border-[hsl(var(--border))] bg-[hsl(var(--primary)/0.06)]"
      >
        {/* Icon */}
        <div className="shrink-0 h-9 w-9 rounded-lg bg-[hsl(var(--primary)/0.12)] flex items-center justify-center">
          <Smartphone className="h-4 w-4 text-[hsl(var(--primary))]" />
        </div>

        {/* Copy */}
        <div className="flex-1 min-w-0">
          <div className="text-sm font-semibold text-foreground leading-tight">Install Derwent CC</div>
          <div className="text-xs text-muted-foreground leading-tight mt-0.5">Download the app for quick access</div>
        </div>

        {/* Download APK button */}
        <Button
          onClick={handleAndroidDownload}
          size="sm"
          className="shrink-0 h-9 px-4 rounded-lg bg-[hsl(var(--primary))] hover:bg-[hsl(var(--primary))]/90 text-[hsl(var(--primary-foreground))] font-semibold text-xs"
          data-testid="install-app-download-apk"
        >
          <Download className="h-3.5 w-3.5 mr-1.5" />
          Install
        </Button>

        {/* Dismiss */}
        <button
          onClick={handleDismiss}
          className="shrink-0 h-7 w-7 rounded-full flex items-center justify-center text-muted-foreground hover:text-foreground hover:bg-[hsl(var(--secondary))] transition-colors"
          aria-label="Dismiss install banner"
          data-testid="install-app-dismiss"
        >
          <XIcon className="h-3.5 w-3.5" />
        </button>
      </div>
    );
  }

  /* ── Chromium slim banner (desktop/other) ── */
  if (!isIOS) {
    return (
      <div
        data-testid="install-app-banner"
        className="relative flex items-center gap-3 px-4 py-2.5 mb-6 rounded-xl border border-[hsl(var(--border))] bg-[hsl(var(--primary)/0.06)]"
      >
        {/* Icon */}
        <div className="shrink-0 h-9 w-9 rounded-lg bg-[hsl(var(--primary)/0.12)] flex items-center justify-center">
          <Download className="h-4 w-4 text-[hsl(var(--primary))]" />
        </div>

        {/* Copy */}
        <div className="flex-1 min-w-0">
          <div className="text-sm font-semibold text-foreground leading-tight">Install Derwent CC</div>
          <div className="text-xs text-muted-foreground leading-tight mt-0.5">Add to your home screen for quick access</div>
        </div>

        {/* Install button */}
        <Button
          onClick={handleInstallChromium}
          disabled={installing}
          size="sm"
          className="shrink-0 h-9 px-4 rounded-lg bg-[hsl(var(--primary))] hover:bg-[hsl(var(--primary))]/90 text-[hsl(var(--primary-foreground))] font-semibold text-xs"
          data-testid="install-app-button"
        >
          <Download className="h-3.5 w-3.5 mr-1.5" />
          {installing ? "Installing…" : "Install"}
        </Button>

        {/* Dismiss */}
        <button
          onClick={handleDismiss}
          className="shrink-0 h-7 w-7 rounded-full flex items-center justify-center text-muted-foreground hover:text-foreground hover:bg-[hsl(var(--secondary))] transition-colors"
          aria-label="Dismiss install banner"
          data-testid="install-app-dismiss"
        >
          <XIcon className="h-3.5 w-3.5" />
        </button>
      </div>
    );
  }

  /* ── iOS slim banner with expandable instructions ── */
  return (
    <div
      data-testid="install-app-banner-ios"
      className="relative mb-6 rounded-xl border border-[hsl(var(--border))] bg-[hsl(var(--primary)/0.06)] overflow-hidden"
    >
      {/* Top bar — always visible */}
      <div className="flex items-center gap-3 px-4 py-2.5">
        {/* Icon */}
        <div className="shrink-0 h-9 w-9 rounded-lg bg-[hsl(var(--primary)/0.12)] flex items-center justify-center">
          <Download className="h-4 w-4 text-[hsl(var(--primary))]" />
        </div>

        {/* Copy */}
        <div className="flex-1 min-w-0">
          <div className="text-sm font-semibold text-foreground leading-tight">Install Derwent CC</div>
          <div className="text-xs text-muted-foreground leading-tight mt-0.5">Add to your home screen in Safari</div>
        </div>

        {/* Expand / collapse */}
        <button
          onClick={() => setIosExpanded((v) => !v)}
          className="shrink-0 inline-flex items-center gap-1 text-xs font-semibold text-[hsl(var(--primary))] hover:underline"
          data-testid="install-ios-toggle"
        >
          {iosExpanded ? "Hide" : "Show me how"}
          {iosExpanded ? (
            <ChevronUp className="h-3.5 w-3.5" />
          ) : (
            <ChevronDown className="h-3.5 w-3.5" />
          )}
        </button>

        {/* Dismiss */}
        <button
          onClick={handleDismiss}
          className="shrink-0 h-7 w-7 rounded-full flex items-center justify-center text-muted-foreground hover:text-foreground hover:bg-[hsl(var(--secondary))] transition-colors"
          aria-label="Dismiss install banner"
          data-testid="install-app-dismiss"
        >
          <XIcon className="h-3.5 w-3.5" />
        </button>
      </div>

      {/* Expandable illustrated steps */}
      {iosExpanded && (
        <div className="border-t border-[hsl(var(--border))] px-4 py-4 space-y-5" data-testid="install-ios-steps">
          {/* Step 1 */}
          <div className="flex items-start gap-3">
            <div className="shrink-0 w-6 h-6 rounded-full bg-[hsl(var(--primary))] text-[hsl(var(--primary-foreground))] flex items-center justify-center text-xs font-bold">
              1
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-sm font-semibold text-foreground mb-2">
                Tap the <span className="text-[hsl(var(--primary))]">Share</span> button
              </div>
              <ShareIllustration />
              <div className="text-xs text-muted-foreground mt-1.5">
                The square with an upward arrow at the bottom of Safari
              </div>
            </div>
          </div>

          {/* Step 2 */}
          <div className="flex items-start gap-3">
            <div className="shrink-0 w-6 h-6 rounded-full bg-[hsl(var(--primary))] text-[hsl(var(--primary-foreground))] flex items-center justify-center text-xs font-bold">
              2
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-sm font-semibold text-foreground mb-2">
                Scroll down and tap <span className="text-[hsl(var(--primary))]">"Add to Home Screen"</span>
              </div>
              <AddToHomeIllustration />
            </div>
          </div>

          {/* Step 3 */}
          <div className="flex items-start gap-3">
            <div className="shrink-0 w-6 h-6 rounded-full bg-[hsl(var(--primary))] text-[hsl(var(--primary-foreground))] flex items-center justify-center text-xs font-bold">
              3
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-sm font-semibold text-foreground mb-2">
                Tap <span className="text-[hsl(var(--primary))]">"Add"</span> in the top-right corner
              </div>
              <TapAddIllustration />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
